import React, { useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, RefreshCw } from 'react-feather';
import styles from '../styles/testMode.module.css';

/**
 * TestMode component
 * Props: test object { title, questions:[{ id?, prompt, choices:[string], correctAnswers:[number|letter|string], explanation? }] }
 * Behavior: exam-like flow with per-question navigation, submit all, score, and review.
 */
const TestMode = ({ test }) => {
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // { [qIdx]: number[] }
  const [submitted, setSubmitted] = useState(false);
  const [showReview, setShowReview] = useState(false);

  const questions = useMemo(() => (Array.isArray(test?.questions) ? test.questions : []), [test?.questions]);
  const title = test?.title || 'Practice Test';

  const normalizeCorrectIndices = (q) => {
    const ans = q?.correctAnswers ?? q?.answers ?? [];
    const choices = q?.choices ?? [];
    if (!Array.isArray(ans)) return [];

    if (ans.every((a) => typeof a === 'number' && Number.isFinite(a))) {
      const max = Math.max(...ans);
      const min = Math.min(...ans);
      const isOneBased = min >= 1 && max === choices.length && !ans.includes(0);
      return ans.map((idx) => {
        const normalized = isOneBased ? idx - 1 : idx;
        return Math.max(0, Math.min(choices.length - 1, normalized));
      });
    }

    const letterToIndex = (s) => {
      const m = String(s).trim().match(/^[A-Za-z]/);
      if (!m) return null;
      const code = m[0].toUpperCase().charCodeAt(0);
      const idx = code - 'A'.charCodeAt(0);
      return idx >= 0 && idx < choices.length ? idx : null;
    };

    const clean = (s) => String(s).trim().toLowerCase().replace(/^[-A-Da-d).\s]+/, '').replace(/\s+/g, ' ');

    return ans
      .map((a) => {
        const byLetter = letterToIndex(a);
        if (byLetter !== null) return byLetter;
        const target = clean(a);
        let idx = choices.findIndex((c) => clean(c) === target);
        if (idx !== -1) return idx;
        idx = choices.findIndex((c) => clean(c).includes(target) || target.includes(clean(c)));
        return idx !== -1 ? idx : -1;
      })
      .filter((i) => i >= 0 && i < choices.length);
  };

  const normalizedKey = useMemo(() => questions.map(normalizeCorrectIndices), [questions]);

  const toggleChoice = (qIdx, choiceIdx) => {
    setAnswers((prev) => {
      const current = prev[qIdx] || [];
      const next = current.includes(choiceIdx)
        ? current.filter((i) => i !== choiceIdx)
        : [...current, choiceIdx];
      return { ...prev, [qIdx]: next };
    });
  };

  const computeScore = () => {
    let correct = 0;
    questions.forEach((q, i) => {
      const key = normalizedKey[i] || [];
      const user = answers[i] || [];
      if (key.length === user.length && key.every((k) => user.includes(k))) correct += 1;
    });
    return { correct, total: questions.length, pct: Math.round((correct / Math.max(1, questions.length)) * 100) };
  };

  if (!questions.length) return <div className={styles.wrapper}>Invalid test data</div>;

  if (submitted) {
    const { correct, total, pct } = computeScore();
    return (
      <div className={styles.wrapper}>
        <div className={styles.resultsCard}>
          <div className={styles.resultsHeader}>
            <h3 className={styles.title}>Test Complete</h3>
            <div className={`${styles.scoreBadge} ${pct >= 70 ? styles.good : styles.needsWork}`}>{pct}%</div>
          </div>
          <div className={styles.resultsBody}>
            <p>
              You answered <strong>{correct}</strong> of <strong>{total}</strong> correctly.
            </p>
            <div className={styles.resultsActions}>
              <button className={styles.primaryBtn} onClick={() => setShowReview((v) => !v)}>
                {showReview ? 'Hide Review' : 'Review Answers'}
              </button>
              <button
                className={styles.secondaryBtn}
                onClick={() => {
                  setSubmitted(false);
                  setShowReview(false);
                  setIndex(0);
                  setAnswers({});
                }}
              >
                <RefreshCw size={16} /> Retake
              </button>
            </div>
          </div>
        </div>

        {showReview && (
          <div className={styles.reviewPanel}>
            {questions.map((q, i) => {
              const key = normalizedKey[i] || [];
              const user = answers[i] || [];
              const correct = key.length === user.length && key.every((k) => user.includes(k));
              return (
                <div key={i} className={`${styles.reviewItem} ${correct ? styles.correct : styles.incorrect}`}>
                  <div className={styles.reviewPrompt}>{q.prompt}</div>
                  <ul className={styles.reviewChoices}>
                    {q.choices.map((c, idx) => (
                      <li key={idx} className={styles.reviewChoice}>
                        <span className={`${styles.badge} ${user.includes(idx) ? styles.user : ''}`}>Your</span>
                        <span className={`${styles.badge} ${key.includes(idx) ? styles.answer : ''}`}>Answer</span>
                        <span className={styles.choiceText}>{c}</span>
                      </li>
                    ))}
                  </ul>
                  {q.explanation && <div className={styles.explanation}>Explanation: {q.explanation}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  const q = questions[index];
  const user = answers[index] || [];
  const multi = (normalizedKey[index] || []).length > 1;

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h3 className={styles.title}>{title}</h3>
        <div className={styles.meta}>
          <div className={styles.counter}>Question {index + 1} / {questions.length}</div>
        </div>
      </div>
      {test?._incomplete && (
        <div className={styles.incompleteBanner} role="status">
          This test appears to be incomplete. Rendering {questions.length} question{questions.length !== 1 ? 's' : ''} that were received.
        </div>
      )}

      <div className={styles.card}>
        <div className={styles.prompt}>{q.prompt}</div>
        <ul className={styles.choiceList}>
          {q.choices.map((c, idx) => (
            <li key={idx} className={styles.choiceItem}>
              <label className={`${styles.choiceLabel} ${user.includes(idx) ? styles.selected : ''}`}>
                <input
                  type="checkbox"
                  checked={user.includes(idx)}
                  onChange={() => toggleChoice(index, idx)}
                />
                <span className={styles.choiceText}>{c}</span>
              </label>
            </li>
          ))}
        </ul>
        <div className={styles.hint}>{multi ? 'Select all that apply' : 'Select one or more answers'}</div>
      </div>

      <div className={styles.controls}>
        <button className={styles.navBtn} onClick={() => setIndex((i) => Math.max(0, i - 1))} disabled={index === 0}>
          <ArrowLeft size={16} /> Previous
        </button>
        <button
          className={`${styles.primaryBtn} ${user.length === 0 ? styles.disabled : ''}`}
          onClick={() => setIndex((i) => Math.min(questions.length - 1, i + 1))}
          disabled={user.length === 0 || index === questions.length - 1}
        >
          Next <ArrowRight size={16} />
        </button>
      </div>

      <div className={styles.footerActions}>
        <button
          className={`${styles.submitBtn} ${Object.keys(answers).length < questions.length ? styles.disabled : ''}`}
          onClick={() => setSubmitted(true)}
          disabled={Object.keys(answers).length < questions.length}
        >
          Submit Test
        </button>
      </div>
    </div>
  );
};

export default TestMode;
